import { useEffect, useRef } from 'react';
import { useToast } from '@withjoy/joykit';
import { useUserConsent } from '@shared/utils/userConsent';
import { useWebsiteEditorDialogContext } from '@website-editor/context/WebsiteEditorDialogContext';
import { ACCOMMODATIONS_DIALOGS } from '../Accommodations.dialogs';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import type { AutoPopulationPollingStatus } from '@shared/utils/autoPopulation/useAutoPopulation.types';
import { addError } from '@shared/utils/logger';
import { useUpdateWebsitePreview } from '../../../hooks/useUpdateWebsitePreview';
import { useAdminStayAreasTelemetry } from '../components/StayAreas/StayAreas.telemetry';

type UseStayAreasTooltipArgs = Readonly<{
  status: AutoPopulationPollingStatus;
  hasAutoPopulatedResults: boolean;
}>;

type UseStayAreasTooltipReturn = Readonly<{
  shouldShowTooltip: boolean;
  handleTooltipClose: () => void;
  /** Whether to show the CTA button in the tooltip */
  showCta: boolean;
  /** Whether the CTA button is loading */
  loading: boolean;
  /** Handler for CTA button click */
  handleCtaClick: () => void;
}>;

export const useStayAreasTooltip = ({ status, hasAutoPopulatedResults }: UseStayAreasTooltipArgs): UseStayAreasTooltipReturn => {
  const { tq } = useTranslation('stayAreasAdmin');
  const { toast } = useToast();
  const telemetry = useAdminStayAreasTelemetry();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
  const { registerDialog, dismiss, isOpen, requestShow } = useWebsiteEditorDialogContext(ACCOMMODATIONS_DIALOGS.STAY_AREAS_TOOLTIP);

  const hasClickedCtaRef = useRef(false);

  // Track previous readiness to detect skip → ready transitions
  const prevReadinessRef = useRef<'loading' | 'ready' | 'skip' | null>(null);

  // Register dialog readiness with the dialog manager
  useEffect(() => {
    let currentReadiness: 'loading' | 'ready' | 'skip';

    if (loadingUserConsent) {
      currentReadiness = 'loading';
      registerDialog('loading');
    } else {
      const hasSeenTooltip = getUserConsent('hasSeenStayAreasTooltip');
      if (status === 'COMPLETED' && hasAutoPopulatedResults && !hasSeenTooltip) {
        currentReadiness = 'ready';
        registerDialog('ready');
      } else {
        currentReadiness = 'skip';
        registerDialog('skip');
      }
    }

    // When transitioning from skip/loading to ready, call requestShow to clear rejection
    if (prevReadinessRef.current !== 'ready' && currentReadiness === 'ready') {
      requestShow();
    }

    prevReadinessRef.current = currentReadiness;
  }, [getUserConsent, loadingUserConsent, status, hasAutoPopulatedResults, registerDialog, requestShow]);

  const handleTooltipClose = useEventCallback(() => {
    dismiss();
    setUserConsent('hasSeenStayAreasTooltip', true);
  });

  const handleCtaClick = useEventCallback(async () => {
    if (hasClickedCtaRef.current) return;
    hasClickedCtaRef.current = true;
    try {
      telemetry.enableStayAreasClicked();
      await setUserConsent('showStayAreasOnGuestSite', true);
      toast(tq`Neighborhoods are now visible to your guests.`);
      updateWebsitePreview();
      handleTooltipClose();
    } catch (error) {
      addError(error, { context: 'StayAreas.handleCtaClick' });
      toast(tq`Failed to enable neighborhoods. Please try again.`);
      hasClickedCtaRef.current = false;
    }
  });

  return {
    shouldShowTooltip: isOpen(),
    handleTooltipClose,
    showCta: hasAutoPopulatedResults && getUserConsent('showStayAreasOnGuestSite') === false,
    loading: hasClickedCtaRef.current,
    handleCtaClick,
  };
};
